import React, { useState } from 'react';
import { OrderStatus, StatusUpdateRequest } from '../../types/order';

interface StatusUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentStatus: OrderStatus;
  allowedNextStatuses: OrderStatus[];
  onSubmit: (request: StatusUpdateRequest) => Promise<void>;
}

const StatusUpdateModal: React.FC<StatusUpdateModalProps> = ({
  isOpen,
  onClose,
  currentStatus,
  allowedNextStatuses,
  onSubmit,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | ''>('');
  const [note, setNote] = useState('');
  const [notifyChannels, setNotifyChannels] = useState<Array<'SMS' | 'EMAIL' | 'WHATSAPP'>>([]);
  const [uploading, setUploading] = useState(false);
  
  if (!isOpen) return null;
  
  const statusLabels: Record<OrderStatus, string> = {
    CREATED: 'Order Created',
    IN_PROGRESS: 'In Tailoring',
    PACKED: 'Packed',
    READY: 'Ready for pickup/delivery',
    COMPLETED: 'Complete',
    ON_HOLD: 'On Hold',
    CANCELLED: 'Cancelled',
  };
  
  // Get all progress statuses (main workflow statuses)
  const progressStatuses: OrderStatus[] = ['CREATED', 'IN_PROGRESS', 'PACKED', 'READY', 'COMPLETED'];
  
  // Show all progress statuses plus special statuses (ON_HOLD, CANCELLED) if allowed
  const availableStatuses = [
    ...progressStatuses.filter(status => allowedNextStatuses.includes(status) || status === currentStatus),
    ...allowedNextStatuses.filter(status => !progressStatuses.includes(status))
  ];
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStatus) return;
    
    setUploading(true);
    try {
      await onSubmit({
        status: selectedStatus as OrderStatus,
        note: note || undefined,
        notifyChannels: notifyChannels.length > 0 ? notifyChannels : undefined,
      });
      onClose();
      // Reset form
      setSelectedStatus('');
      setNote('');
      setNotifyChannels([]);
    } catch (_error) {
      console.error('Failed to update status:', _error);
    } finally {
      setUploading(false);
    }
  };
  
  const toggleChannel = (channel: 'SMS' | 'EMAIL' | 'WHATSAPP') => {
    setNotifyChannels((prev) =>
      prev.includes(channel)
        ? prev.filter((c) => c !== channel)
        : [...prev, channel]
    );
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="status-modal-title"
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 id="status-modal-title" className="text-xl font-semibold text-gray-900">Update Order Status</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close modal"
            >
              ✕
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Current Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Status
              </label>
              <p className="text-sm text-gray-900 font-medium">{statusLabels[currentStatus]}</p>
            </div>
            
            {/* New Status Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Status *
              </label>
              <div className="space-y-2">
                {availableStatuses.map((status) => {
                  const isCurrent = status === currentStatus;
                  const isDisabled = isCurrent || (!allowedNextStatuses.includes(status) && progressStatuses.includes(status));
                  
                  return (
                    <label
                      key={status}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                        isDisabled
                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                          : status === selectedStatus
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="status"
                        value={status}
                        checked={selectedStatus === status}
                        onChange={(e) => !isDisabled && setSelectedStatus(e.target.value as OrderStatus)}
                        disabled={isDisabled}
                        className="mr-3"
                      />
                      <div className="flex-1">
                        <span className={`text-sm ${isCurrent ? 'font-semibold text-blue-600' : 'text-gray-900'}`}>
                          {statusLabels[status]}
                        </span>
                        {isCurrent && (
                          <span className="ml-2 text-xs text-gray-500">(Current)</span>
                        )}
                      </div>
                    </label>
                  );
                })}
                {availableStatuses.length === 0 && (
                  <p className="text-sm text-gray-500">
                    No status transitions available (terminal state)
                  </p>
                )}
              </div>
            </div>
            
            {/* Note */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message to Customer (Optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add a note about this status change..."
              />
            </div>
            
            {/* Notification Channels */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notify Customer Via
              </label>
              <div className="space-y-2">
                {(['SMS', 'EMAIL', 'WHATSAPP'] as const).map((channel) => (
                  <label key={channel} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifyChannels.includes(channel)}
                      onChange={() => toggleChannel(channel)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">{channel}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={uploading}
                aria-label="Cancel status update"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!selectedStatus || uploading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Update order status"
              >
                {uploading ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StatusUpdateModal;

